import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getCart, saveCart } from '../utils/cart'
import { mapCartToPartner } from '../partnerSchema'
import { API_BASE, RAZORPAY_KEY } from '../config'

export default function CartPage({ cart = [], onRemove, onClear, onAdd, onBuy, onPlaceOrder }){
  const navigate = useNavigate()
  const total = (cart || []).reduce((s, it) => s + ((it.price || 0) * (it.quantity || 1)), 0)

  const localPlaceOrder = async (cartItems) => {
    if (!cartItems || !cartItems.length) {
      try{ window.alert('Cart is empty') }catch(e){}
      return
    }
    try{
      const payload = { cart: cartItems, total }
      const res = await fetch(`${API_BASE}/api/payments/process`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      if (!res.ok) throw new Error('Payment API failed')
      const j = await res.json()
      if (j && j.redirectUrl) {
        // Redirect user to checkout page (mock)
        window.location.href = j.redirectUrl
        return
      }
      try{ window.alert('Order placed (mock)') }catch(e){}
    }catch(e){ console.error('Could not place order', e); try{ window.alert('Could not place order') }catch(_){} }
  }

  // Load Razorpay checkout script if not already present
  const loadRazorpay = () => {
    return new Promise((resolve, reject) => {
      if (window && window.Razorpay) return resolve()
      const id = 'razorpay-sdk'
      if (document.getElementById(id)) {
        // wait a tick
        return setTimeout(() => { window.Razorpay ? resolve() : reject(new Error('Razorpay failed to load')) }, 500)
      }
      const script = document.createElement('script')
      script.id = id
      script.src = 'https://checkout.razorpay.com/v1/checkout.js'
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Could not load Razorpay script'))
      document.body.appendChild(script)
    })
  }

  const payWithRazorpay = async (cartItems) => {
    if (!RAZORPAY_KEY) {
      try{ window.alert('Razorpay key not configured. Set REACT_APP_RAZORPAY_KEY in environment.') }catch(e){}
      return
    }
    if (!cartItems || !cartItems.length) {
      try{ window.alert('Cart is empty') }catch(e){}
      return
    }

    try{
      await loadRazorpay()
    }catch(e){ console.error('Razorpay load error', e); try{ window.alert('Could not load payment widget') }catch(_){}; return }

    // Create an order server-side (recommended) and include order_id in options
    let orderId = null
    try{
      const createRes = await fetch(`${API_BASE}/api/razorpay/create-order`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ amount: Math.round((total || 0) * 100), currency: 'INR' }) })
      if (createRes.ok) {
        const oj = await createRes.json()
        orderId = oj && (oj.id || oj.order_id || oj.order_id) || null
      } else {
        console.warn('Could not create server order for Razorpay', createRes.status)
      }
    }catch(e){ console.error('Error creating server razorpay order', e) }

    const options = {
      key: RAZORPAY_KEY,
      amount: Math.round((total || 0) * 100), // in paise
      currency: 'INR',
      name: 'Revive',
      description: 'Order payment',
      order_id: orderId,
      handler: async function (response) {
        // response contains razorpay_payment_id, razorpay_order_id, razorpay_signature
        try{
          const payload = { cart: cartItems, total, gateway: 'razorpay', paymentId: response.razorpay_payment_id, razorpay_order_id: response.razorpay_order_id, razorpay_signature: response.razorpay_signature, status: 'success' }
          await fetch(`${API_BASE}/api/payments/process`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
        }catch(err){ console.error('Could not record payment', err) }
        // Redirect to order complete page
        try{ window.location.href = `/order-complete?paymentId=${encodeURIComponent(response.razorpay_payment_id||'')}&status=success&amount=${encodeURIComponent(total||'')}` }catch(e){ }
      },
      modal: { ondismiss: function(){ /* user dismissed */ } }
    }

    try{
      const rzp = new window.Razorpay(options)
      rzp.open()
    }catch(e){ console.error('Razorpay open error', e); try{ window.alert('Could not open payment window') }catch(_){} }
  }

  const payWithZoho = async (cartItems) => {
    if (!cartItems || !cartItems.length) {
      try{ window.alert('Cart is empty') }catch(e){}
      return
    }
    try{
      const payload = { cart: cartItems, total, currency: 'INR', returnUrl: window.location.origin + '/order-complete' }
      const res = await fetch(`${API_BASE}/api/zoho/create-checkout`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const j = await res.json()
      if (res.ok && j && j.redirectUrl) {
        window.location.href = j.redirectUrl
        return
      }
      // If Zoho endpoint not configured or errored, fall back to local place order
      if (!res.ok) {
        try{ console.warn('Zoho create-checkout failed', j) }catch(_){}
      }
      try{ window.alert('Could not start Zoho checkout — using fallback order flow') }catch(e){}
      localPlaceOrder(cartItems)
    }catch(e){ console.error('Zoho payment error', e); try{ window.alert('Payment failed') }catch(_){} }
  }

  const updateQuantity = (item, nextQty) => {
    try{
      const raw = JSON.parse(localStorage.getItem('auth_user')||'null')
      const pid = raw && raw.role === 'patient' ? raw.email : undefined
      const current = getCart(pid) || []
      const next = current.map(c => {
        if ((c.productId && item.productId && c.productId === item.productId) || c.name === item.name) {
          return { ...c, quantity: nextQty }
        }
        return c
      }).filter(c => {
        const q = (typeof c.quantity === 'number') ? c.quantity : (c.quantity ? Number(c.quantity) : 1)
        return q > 0
      })
      saveCart(next, pid)
    }catch(e){
      const current = getCart() || []
      const next = current.map(c => {
        if ((c.productId && item.productId && c.productId === item.productId) || c.name === item.name) {
          return { ...c, quantity: nextQty }
        }
        return c
      }).filter(c => {
        const q = (typeof c.quantity === 'number') ? c.quantity : (c.quantity ? Number(c.quantity) : 1)
        return q > 0
      })
      saveCart(next)
    }
  }

  return (
    <section className="py-12">
      <div className="container-custom mx-auto px-6">
        <h3 className="text-2xl font-bold mb-6">Cart</h3>

        {(!cart || cart.length === 0) ? (
          <div className="text-gray-600">Your cart is empty. <Link to="/">Shop now</Link></div>
        ) : (
          <>
            <div className="space-y-6">
              {cart.map((it, idx) => (
                <div key={idx} className="bg-white p-6 rounded shadow-sm grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <div className="md:col-span-1 flex items-center justify-center">
                    {it.imgs && it.imgs[0] ? (
                      <img src={it.imgs[0]} alt={it.name} className="w-[60vw] max-w-[380px] h-[50vh] max-h-[540px] object-contain" />
                    ) : (
                      <div className="w-[60vw] max-w-[380px] h-[50vh] max-h-[540px] bg-gray-100 flex items-center justify-center">No image</div>
                    )}
                  </div>

                  <div className="md:col-span-1">
                    <div className="flex items-center justify-between">
                      <h2 className="text-2xl font-semibold">{it.name}</h2>
                      <div className="text-sm text-gray-600">{it.reviews ? `${it.reviews} Reviews` : ''}</div>
                    </div>

                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-green-600 text-sm">✓ Broad-Spectrum Protection</span>
                      <span className="text-green-600 text-sm">✓ Light Gel Texture</span>
                      <span className="text-green-600 text-sm">✓ Daily Sun Care</span>
                    </div>

                    <div className="mt-4 flex items-end gap-6">
                      <div>
                        {it.price ? (
                          <div className="text-3xl font-bold">₹{it.price}</div>
                        ) : null}
                        {it.oldPrice ? (
                          <div className="text-sm text-gray-500 line-through">₹{it.oldPrice}</div>
                        ) : null}
                        <div className="text-sm text-gray-700 mt-1">Subtotal: <span className="font-semibold">₹{(it.price||0) * (it.quantity||1)}</span></div>
                        
                      </div>

                      <div className="ml-auto text-right">
                        <div className="text-sm text-gray-600">Pack: 50ml</div>
                        <div className="mt-2 inline-block bg-gray-200 px-3 py-1 rounded">50ml</div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="flex items-center gap-4">
                        <div className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 overflow-hidden shadow-sm">
                          <button aria-label="decrease" onClick={() => updateQuantity(it, Math.max(0, (it.quantity||1) - 1))} className="h-9 w-9 flex items-center justify-center text-lg text-gray-600 hover:bg-gray-100">−</button>
                          <div className="h-9 w-10 flex items-center justify-center text-sm font-medium text-gray-800">{it.quantity || 1}</div>
                          <button aria-label="increase" onClick={() => updateQuantity(it, (it.quantity||1) + 1)} className="h-9 w-9 flex items-center justify-center text-lg text-gray-600 hover:bg-gray-100">+</button>
                        </div>

                        <div className="flex items-center gap-2">
                          <button onClick={() => onRemove && onRemove(it)} className="px-3 py-1.5 text-sm font-medium text-red-500 border border-red-100 rounded-lg hover:bg-red-50">Remove</button>
                        </div>
                      </div>
                    </div>

                    
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="text-lg font-semibold">Total: ₹{total}</div>
              <div className="flex gap-3">
                <button onClick={() => { if (onPlaceOrder) { onPlaceOrder(cart) } else { localPlaceOrder(cart) } }} className="px-4 py-2 bg-brand text-white rounded">Place order</button>
                <button onClick={() => payWithRazorpay(cart)} className="px-4 py-2 border rounded">Pay with Razorpay</button>
                <button onClick={() => payWithZoho(cart)} className="px-4 py-2 border rounded">Pay with Zoho</button>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}
